import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Clock, Settings2 } from 'lucide-react';

interface AutoAcceptControlModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlatform: 'efood' | 'box' | 'wolt' | 'all';
  isAutoAcceptEnabled: boolean;
  currentDeliveryTime: number;
  onUpdateSettings: (enabled: boolean, deliveryTime: number, platform?: 'efood' | 'box' | 'wolt' | 'all') => Promise<void>;
  platformSettings?: {
    efood: { enabled: boolean; deliveryTime: number };
    box: { enabled: boolean; deliveryTime: number };
    wolt: { enabled: boolean; deliveryTime: number };
  };
}

const AutoAcceptControlModal: React.FC<AutoAcceptControlModalProps> = ({
  isOpen,
  onClose,
  currentPlatform,
  isAutoAcceptEnabled,
  currentDeliveryTime,
  onUpdateSettings,
  platformSettings
}) => {
  const { t } = useTranslation(['orders', 'common']);

  const [selectedTime, setSelectedTime] = useState(currentDeliveryTime);
  const [customTime, setCustomTime] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<'efood' | 'box' | 'wolt' | 'all'>('all');
  const [error, setError] = useState<string | null>(null);

  const platformColors: Record<string, { active: string; pill: string }> = {
    all: { active: 'bg-purple-600', pill: 'bg-purple-600' },
    efood: { active: 'bg-red-600', pill: 'bg-red-600' },
    box: { active: 'bg-orange-600', pill: 'bg-orange-600' },
    wolt: { active: 'bg-blue-600', pill: 'bg-blue-600' },
  };

  const platformNames: Record<string, string> = {
    all: t('orders:autoAcceptControl.allPlatforms'),
    efood: 'Efood',
    box: 'Box',
    wolt: 'Wolt',
  };

  const presetTimes = [15, 20, 30, 45, 60, 90];

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedTime(currentDeliveryTime);
      setCustomTime('');
      setIsCustom(false);
      setSelectedPlatform('all');
      setError(null);
    }
  }, [isOpen, currentDeliveryTime]);

  // Update selected time when platform changes
  useEffect(() => {
    if (selectedPlatform !== 'all' && platformSettings) {
      const settings = platformSettings[selectedPlatform];
      setSelectedTime(settings.deliveryTime);
      setIsCustom(!presetTimes.includes(settings.deliveryTime));
      if (!presetTimes.includes(settings.deliveryTime)) {
        setCustomTime(settings.deliveryTime.toString());
      }
    } else if (selectedPlatform === 'all') {
      setSelectedTime(currentDeliveryTime);
      setIsCustom(!presetTimes.includes(currentDeliveryTime));
      if (!presetTimes.includes(currentDeliveryTime)) {
        setCustomTime(currentDeliveryTime.toString());
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlatform, platformSettings, currentDeliveryTime]);

  const handlePresetTimeClick = (time: number) => {
    setSelectedTime(time);
    setCustomTime('');
    setIsCustom(false);
  };

  const handleCustomTimeChange = (value: string) => {
    setCustomTime(value);
    setIsCustom(true);
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 5 && numValue <= 180) {
      setSelectedTime(numValue);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const timeToSave = isCustom ? parseInt(customTime) : selectedTime;
      await onUpdateSettings(true, timeToSave, selectedPlatform);
      onClose();
    } catch (err) {
      console.error('Failed to update settings:', err);
      setError(t('orders:autoAcceptControl.updateFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await onUpdateSettings(false, currentDeliveryTime, selectedPlatform);
      onClose();
    } catch (err) {
      console.error('Failed to disable auto-accept:', err);
      setError(t('orders:autoAcceptControl.disableFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentStatus = () => {
    if (selectedPlatform === 'all') return isAutoAcceptEnabled;
    if (platformSettings) return platformSettings[selectedPlatform].enabled;
    return false;
  };

  if (!isOpen) return null;

  const isEnabled = getCurrentStatus();
  const pc = platformColors[selectedPlatform] || platformColors.all;

  return (
    <div className="cashier-overlay fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="cashier-dialog bg-surface-1 rounded-xl shadow-2xl border border-line w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="cashier-dialog-header flex items-center justify-between px-5 py-4 border-b border-line">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Settings2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-base font-bold text-t1">{t('orders:autoAcceptControl.settingsTitle')}</h2>
              <p className="text-xs text-t3">{t('orders:autoAcceptControl.settingsSubtitle')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-t3 hover:text-t1 hover:bg-surface-2 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Platform Selector */}
          <div>
            <label className="block text-xs font-semibold text-t3 uppercase tracking-wider mb-2">
              {t('orders:autoAcceptControl.platformLabel')}
            </label>
            <div className="grid grid-cols-4 gap-1.5">
              {(['all', 'efood', 'box', 'wolt'] as const).map(p => (
                <button
                  key={p}
                  onClick={() => setSelectedPlatform(p)}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                    selectedPlatform === p
                      ? `${platformColors[p].pill} text-white`
                      : 'bg-surface-2 text-t2 hover:bg-surface-3'
                  }`}
                >
                  {platformNames[p]}
                </button>
              ))}
            </div>
          </div>

          {/* Current Status */}
          <div className={`flex items-center justify-between p-3 rounded-lg border ${
            isEnabled ? 'bg-green-500/10 border-green-500/30' : 'bg-surface-2 dark:bg-surface-2/50 border-line-strong'
          }`}>
            <div>
              <p className="text-xs text-t3">{platformNames[selectedPlatform]} {t('orders:fiscalViewer.status')}</p>
              <p className={`text-sm font-bold ${isEnabled ? 'text-green-600 dark:text-green-400' : 'text-t2'}`}>
                {isEnabled ? t('orders:autoAcceptControl.statusActive') : t('orders:autoAcceptControl.statusInactive')}
              </p>
            </div>
            {isEnabled && (
              <div className="text-right">
                <p className="text-xs text-t3">{t('orders:autoAcceptControl.deliveryTimeLabel')}</p>
                <p className="text-sm font-bold text-blue-600 dark:text-blue-400">
                  {selectedPlatform === 'all'
                    ? currentDeliveryTime
                    : platformSettings?.[selectedPlatform]?.deliveryTime || currentDeliveryTime} min
                </p>
              </div>
            )}
          </div>

          {/* Time Selection */}
          <div>
            <label className="block text-xs font-semibold text-t3 uppercase tracking-wider mb-2">
              {t('orders:autoAcceptControl.deliveryTimeLabel')}
            </label>
            <div className="grid grid-cols-3 gap-1.5 mb-3">
              {presetTimes.map((time) => (
                <button
                  key={time}
                  onClick={() => handlePresetTimeClick(time)}
                  className={`py-2.5 rounded-lg text-sm font-semibold transition-all ${
                    selectedTime === time && !isCustom
                      ? `${pc.active} text-white`
                      : 'bg-surface-2 text-t2 border border-line-strong hover:border-line-strong'
                  }`}
                >
                  {time} min
                </button>
              ))}
            </div>

            {/* Custom Time */}
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="5"
                max="180"
                value={customTime}
                onChange={(e) => handleCustomTimeChange(e.target.value)}
                placeholder={t('orders:autoAcceptControl.customTimePlaceholder')}
                className="flex-1 px-3 py-2 bg-surface-2 border border-line-strong rounded-lg text-sm text-t1 placeholder-t3 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <span className="text-xs text-t3">min</span>
            </div>
          </div>

          {/* Selected Time Summary */}
          {selectedTime > 0 && (
            <div className="flex items-center gap-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                {t('orders:autoAcceptControl.deliveryTimeSummary', { time: selectedTime })}
              </span>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-line flex gap-2">
          {isEnabled ? (
            <>
              <button
                onClick={handleSave}
                disabled={isLoading || (isCustom && (!customTime || parseInt(customTime) < 5 || parseInt(customTime) > 180))}
                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold text-t1 ${pc.active} hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                {isLoading ? t('orders:autoAcceptControl.updatingBtn') : t('orders:autoAcceptControl.updateBtn')}
              </button>
              <button
                onClick={handleDisable}
                disabled={isLoading}
                className="px-4 py-2.5 rounded-lg text-sm font-semibold text-red-600 dark:text-red-400 bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 transition-colors disabled:opacity-40"
              >
                {t('orders:autoAcceptControl.disableBtn')}
              </button>
            </>
          ) : (
            <button
              onClick={handleSave}
              disabled={isLoading || (isCustom && (!customTime || parseInt(customTime) < 5 || parseInt(customTime) > 180))}
              className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-green-600 hover:bg-green-700 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isLoading
                ? t('orders:autoAcceptControl.enablingBtn')
                : t('orders:autoAcceptControl.enableBtn', { platform: platformNames[selectedPlatform] })}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutoAcceptControlModal;
